import * as React from "react";
import { usePortalSeo, useEnrolmentUiConfig } from "../config";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError, type Enrolment, type Program } from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  PageHeader,
  Section,
} from "@alight/ui";
import { Mail } from "lucide-react";

/**
 * "My registrations" — the passwordless parent view. Signed out: a one-field
 * magic-link sign-in (redirects back here). Signed in: every registration for
 * the session email with its live payment status, plus sign-out.
 */

const api = apiClient("");

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const STATUS_LABEL: Record<string, { label: string; variant: "accent" | "muted" | "outline" }> = {
  unpaid: { label: "Pending payment", variant: "muted" },
  pending_review: { label: "Pending verification", variant: "accent" },
  partial: { label: "Partially paid", variant: "accent" },
  paid: { label: "Paid — confirmed", variant: "outline" },
  refunded: { label: "Refunded", variant: "muted" },
  cancelled: { label: "Cancelled", variant: "muted" },
};

const METHOD_LABEL: Record<string, string> = {
  payid: "Bank transfer",
  bank_transfer: "Bank transfer",
  stripe: "Card",
  cash: "Cash",
  already: "Already paid",
  other: "Other",
};

/** Friendly labels for well-known `extra` keys; anything else is humanised. */
const EXTRA_LABEL: Record<string, string> = {
  cash_when: "Cash handover",
  already_note: "Payment note",
};

function humanise(name: string): string {
  const words = name.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function MyPage() {
  usePortalSeo("My Registrations");
  const { registerPath } = useEnrolmentUiConfig();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["auth", "me"], queryFn: () => api.authMe(), retry: false });
  const email = me.data?.email ?? null;

  const list = useQuery({
    queryKey: ["my", "enrolments"],
    queryFn: () => api.myEnrolments(),
    enabled: email !== null,
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
  });

  const programsQuery = useQuery({
    queryKey: ["programs"],
    queryFn: () => api.getPrograms(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const draftQuery = useQuery({
    queryKey: ["my", "draft"],
    queryFn: () => api.getDraft(),
    enabled: email !== null,
    retry: false,
  });
  const draft = draftQuery.data ?? null;

  async function signOut() {
    await api.signOut().catch(() => {});
    await qc.invalidateQueries({ queryKey: ["auth", "me"] });
    await qc.invalidateQueries({ queryKey: ["my", "enrolments"] });
  }

  return (
    <>
      <PageHeader
        eyebrow="Your account"
        title="My registrations"
        description="View the status of your enrolments — no password needed, we email you a sign-in link."
      />
      <Section tone="paper" spacing="lg">
        <Container width="narrow">
          {email === null ? (
            <SignInCard />
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted">
                  Signed in as <strong className="text-ink">{email}</strong>
                </p>
                <button
                  type="button"
                  className="text-xs font-semibold text-muted underline underline-offset-2 hover:text-ink"
                  onClick={() => void signOut()}
                >
                  Sign out
                </button>
              </div>

              <MyDetailsCard />

              {draft && Object.keys(draft.data).length > 0 && (
                <Card className="border-primary/40 bg-primary/5">
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
                    <div>
                      <p className="text-sm font-semibold text-ink">Resume your registration</p>
                      <p className="text-xs text-muted">
                        You have an enrolment in progress — pick up where you left off.
                      </p>
                    </div>
                    <Button asChild size="sm">
                      <Link to={registerPath}>Resume</Link>
                    </Button>
                  </CardContent>
                </Card>
              )}

              {list.isLoading ? (
                <p className="text-sm text-muted">Loading your registrations…</p>
              ) : (list.data ?? []).length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center sm:p-8">
                    <p className="mb-4 text-sm text-muted">
                      No registrations yet for this email.
                    </p>
                    <Button asChild variant="accent">
                      <Link to={registerPath}>Enrol now</Link>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {(list.data ?? []).map((e) => (
                    <RegistrationCard
                      key={e.id}
                      enrolment={e}
                      program={(programsQuery.data ?? []).find(
                        (p) => String(p.id) === e.program_id,
                      )}
                    />
                  ))}
                  <Button asChild variant="outline" className="w-full">
                    <Link to={registerPath}>Enrol another child</Link>
                  </Button>
                </>
              )}
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}

/**
 * The parent's saved details (name + phone). Written automatically on each
 * enrolment, editable here, and prefilled into the next /enrol journey.
 */
function MyDetailsCard() {
  const qc = useQueryClient();
  const profileQuery = useQuery({
    queryKey: ["my", "profile"],
    queryFn: () => api.myProfile(),
    retry: false,
  });
  const profile = profileQuery.data?.profile ?? null;

  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function startEdit() {
    setName(profile?.name ?? "");
    setPhone(profile?.phone ?? "");
    setError(null);
    setEditing(true);
  }

  async function save() {
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // A blank phone clears the saved one (the field is optional).
      await api.updateMyProfile({
        name: name.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      await qc.invalidateQueries({ queryKey: ["my", "profile"] });
      setEditing(false);
    } catch {
      setError("We couldn't save your details — please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (profileQuery.isLoading || profileQuery.isError) return null;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-ink">My details</h2>
            <p className="text-sm text-muted">
              We prefill these next time you enrol a child.
            </p>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={startEdit}>
              {profile?.name ? "Edit" : "Add details"}
            </Button>
          )}
        </div>
        {!editing ? (
          <dl className="mt-4 space-y-2.5">
            <DetailRow label="Parent / guardian" value={profile?.name ?? "—"} />
            <DetailRow label="Phone" value={profile?.phone ?? "—"} />
          </dl>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="profile-name">Parent / guardian name</Label>
              <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-phone">Phone</Label>
              <Input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            {error && (
              <p role="alert" className="text-sm font-medium text-accent">
                {error}
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" disabled={saving} onClick={() => void save()}>
                {saving ? "Saving…" : "Save details"}
              </Button>
              <Button size="sm" variant="outline" disabled={saving} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SignInCard() {
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [devLink, setDevLink] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  // Unlock the page the moment the emailed link is clicked (any tab).
  React.useEffect(() => {
    if (!sent) return;
    const iv = setInterval(() => {
      void api.authMe().then((r) => {
        if (r.email) void qc.invalidateQueries({ queryKey: ["auth", "me"] });
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(iv);
  }, [sent, qc]);

  async function request() {
    setError(null);
    setBusy(true);
    try {
      const r = await api.requestLink({ email: email.trim().toLowerCase(), redirect: "/my" });
      setSent(true);
      setDevLink(r.devLink ?? null);
      if (!r.emailed && !r.devLink) setError("We couldn't send the email just now — try again shortly.");
    } catch {
      setError("We couldn't send the sign-in link. Check the address and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="p-6 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor="my-email">Email</Label>
          <Input
            id="my-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSent(false);
              setDevLink(null);
            }}
          />
          <p className="text-xs text-muted">
            Use the email you registered with — we&apos;ll send a one-time sign-in link.
          </p>
        </div>
        {error && (
          <p role="alert" className="mt-3 text-sm font-medium text-accent">
            {error}
          </p>
        )}
        {!sent ? (
          <Button
            className="mt-4 w-full"
            size="lg"
            disabled={!EMAIL_RE.test(email) || busy}
            onClick={() => void request()}
          >
            <Mail className="h-4 w-4" />
            {busy ? "Sending…" : "Email me a sign-in link"}
          </Button>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="rounded-card border border-dashed border-border bg-paper px-4 py-3 text-sm text-muted">
              Sign-in link sent to <strong className="text-ink">{email}</strong> — click it and
              this page unlocks automatically.
            </div>
            {devLink && (
              <Button asChild variant="outline" size="sm" className="w-full">
                <a href={devLink}>(Dev) Open the sign-in link</a>
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RegistrationCard({ enrolment, program }: { enrolment: Enrolment; program?: Program }) {
  const status = STATUS_LABEL[enrolment.payment_status] ?? {
    label: enrolment.payment_status,
    variant: "muted" as const,
  };
  const extra = (enrolment.extra ?? {}) as Record<string, unknown>;
  const cashWhen = typeof extra.cash_when === "string" ? extra.cash_when : null;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-ink">{enrolment.child_name}</h2>
            <p className="text-sm text-muted">
              {program ? `${program.title} · ` : ""}
              {enrolment.year_level} · Reference{" "}
              <strong className="text-ink">{enrolment.payment_ref}</strong>
            </p>
          </div>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>

        <p className="mt-3 text-sm text-muted">
          {enrolment.payment_status === "unpaid" && enrolment.payment_method === "cash" && cashWhen
            ? `Cash handover: ${cashWhen.toLowerCase()} — the team will receipt it on the spot.`
            : enrolment.payment_status === "unpaid"
              ? "We're waiting on your payment — tap “Payment options” to pay by bank transfer or cash, or to tell us you've already paid."
              : enrolment.payment_status === "pending_review"
                ? "We've been told to expect your payment and will confirm by email once it's matched."
                : enrolment.payment_status === "partial"
                  ? "We've received part of your payment — the place is confirmed once the balance arrives."
                  : enrolment.payment_status === "paid"
                    ? "All paid — this place is confirmed. See you there!"
                    : null}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {/* Anything still owing gets a route to the bank details — including
              imported enrolments, which have no payment_method at all. Cash is
              the one method where a transfer page would mislead. */}
          {(enrolment.payment_status === "unpaid" || enrolment.payment_status === "partial") &&
            enrolment.payment_method !== "cash" && (
              <Button asChild variant="outline" size="sm">
                <Link to="/pay/$ref" params={{ ref: enrolment.payment_ref }}>
                  Payment options
                </Link>
              </Button>
            )}
          <ReviewDetailsDialog enrolment={enrolment} program={program} extra={extra} />
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewDetailsDialog({
  enrolment,
  program,
  extra,
}: {
  enrolment: Enrolment;
  program?: Program;
  extra: Record<string, unknown>;
}) {
  const method = enrolment.payment_method
    ? METHOD_LABEL[enrolment.payment_method] ?? enrolment.payment_method
    : null;
  const extraRows = Object.entries(extra).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Review details
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registration details</DialogTitle>
          <DialogDescription>
            Reference {enrolment.payment_ref} — submitted{" "}
            {new Date(enrolment.created_at).toLocaleDateString()}.
          </DialogDescription>
        </DialogHeader>
        <dl className="space-y-2.5">
          <DetailRow label="Parent / guardian" value={enrolment.parent_name} />
          {enrolment.parent_phone && <DetailRow label="Phone" value={enrolment.parent_phone} />}
          <DetailRow label="Child" value={enrolment.child_name} />
          <DetailRow label="Year level" value={enrolment.year_level} />
          {program && <DetailRow label="Program" value={program.title} />}
          {method && <DetailRow label="Payment method" value={method} />}
          <DetailRow label="Amount" value={`$${(enrolment.amount_cents / 100).toFixed(0)}`} />
          {extraRows.map(([key, value]) => (
            <DetailRow
              key={key}
              label={EXTRA_LABEL[key] ?? humanise(key)}
              value={typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
            />
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 text-xs font-bold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-right text-sm text-ink">{value}</dd>
    </div>
  );
}
